import { DotsThree } from "@phosphor-icons/react"

import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { MenuButton } from "@tokenterminal/core/ui/menu/MenuButton"
import { type PresetColumnConfig } from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import { type SetStateAction, type Dispatch, useMemo } from "react"
import {
  getAggregationAndTimeframeFromValue,
  getGroupOptionFromTimeframeAndAggregation,
  getSplitGroupOptions,
} from "./utils"

const uppercaseFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const ColumnSettingsDropdown = ({
  item,
  index,
  setDraftSelectedColumns,
}: {
  item: {
    label: string | undefined
    value: string | undefined
    group?: string | undefined
    sparkline: boolean
    groupOptions: {
      label: string
      value: string
    }[]
  }
  index: number
  setDraftSelectedColumns: Dispatch<SetStateAction<PresetColumnConfig[]>>
}) => {
  const splitGroupOptions = useMemo(() => getSplitGroupOptions(item), [item])

  const handleSelectTimeframe = (option: string) => {
    const newTimeframeValue = option

    if (newTimeframeValue === "Latest") {
      const latestOption = item.groupOptions.find(
        (option) => option.label === "Latest"
      )
      if (latestOption) {
        setDraftSelectedColumns((prev) => {
          return prev.map((c, i) => {
            if (i === index) {
              return {
                ...c,
                field: latestOption.value,
                sparkline: false,
              }
            }
            return c
          })
        })
      }
      return

      // If current aggregation is ATH or ATL, default to "avg"
    } else if (
      derivedAggregationValue === "ATH" ||
      derivedAggregationValue === "ATL"
    ) {
      const newOption = getGroupOptionFromTimeframeAndAggregation(
        newTimeframeValue,
        "avg",
        item.groupOptions
      )
      if (newOption) {
        setDraftSelectedColumns((prev) => {
          return prev.map((c, i) => {
            if (i === index) {
              return { ...c, field: newOption.value, sparkline: false }
            }
            return c
          })
        })
      }
      return

      // If there's no aggregation selected, default to the first aggregation
    } else if (!derivedAggregationValue) {
      const firstAggregation = Array.from(splitGroupOptions.aggregations)[0]

      if (firstAggregation) {
        const newOption = getGroupOptionFromTimeframeAndAggregation(
          newTimeframeValue,
          firstAggregation,
          item.groupOptions
        )

        if (newOption) {
          setDraftSelectedColumns((prev) => {
            return prev.map((c, i) => {
              if (i === index) {
                return {
                  ...c,
                  field: newOption.value,
                  sparkline: false,
                }
              }
              return c
            })
          })
        }
      }
    } else {
      const newOption = getGroupOptionFromTimeframeAndAggregation(
        newTimeframeValue,
        derivedAggregationValue,
        item.groupOptions
      )
      if (newOption) {
        setDraftSelectedColumns((prev) => {
          return prev.map((c, i) => {
            if (i === index) {
              let sparkline = c.sparkline

              if (
                newOption.value.includes("24h") ||
                newOption.value.includes("latest")
              ) {
                sparkline = false
              }

              return { ...c, field: newOption.value, sparkline }
            }
            return c
          })
        })
      }
    }
  }

  const handleSelectAggregation = (option: string) => {
    const newAggregationValue = option

    // If current aggregation is ATH or ATL, default to "avg"
    if (newAggregationValue === "ATH" || newAggregationValue === "ATL") {
      const specialOption = item.groupOptions.find(
        (option) => option.label === newAggregationValue
      )
      if (specialOption) {
        setDraftSelectedColumns((prev) => {
          return prev.map((c, i) => {
            if (i === index) {
              return {
                ...c,
                field: specialOption.value,
                sparkline: false,
              }
            }
            return c
          })
        })
      }
      return

      // If current timeframe is "Latest", default timeframe to "24h"
    } else if (derivedTimeframeValue === "Latest") {
      const newOption = getGroupOptionFromTimeframeAndAggregation(
        "24h",
        newAggregationValue,
        item.groupOptions
      )
      if (newOption) {
        setDraftSelectedColumns((prev) => {
          return prev.map((c, i) => {
            if (i === index) {
              return { ...c, field: newOption.value, sparkline: false }
            }
            return c
          })
        })
      }
      return

      // If there's no timeframe selected, default to the first timeframe
    } else if (!derivedTimeframeValue) {
      const firstTimeframe = Array.from(splitGroupOptions.timeframes)[0]
      if (firstTimeframe) {
        const newOption = getGroupOptionFromTimeframeAndAggregation(
          firstTimeframe,
          newAggregationValue,
          item.groupOptions
        )
        if (newOption) {
          setDraftSelectedColumns((prev) => {
            return prev.map((c, i) => {
              if (i === index) {
                return {
                  ...c,
                  field: newOption.value,
                  sparkline: false,
                }
              }
              return c
            })
          })
        }
      }
    } else {
      const newOption = getGroupOptionFromTimeframeAndAggregation(
        derivedTimeframeValue,
        newAggregationValue,
        item.groupOptions
      )
      if (newOption) {
        setDraftSelectedColumns((prev) => {
          return prev.map((c, i) => {
            if (i === index) {
              return { ...c, field: newOption.value, sparkline: false }
            }
            return c
          })
        })
      }
    }
  }

  const derivedAggregationValue = useMemo(() => {
    const aggregation = getAggregationAndTimeframeFromValue(
      item.value,
      item.groupOptions
    )?.aggregation

    return aggregation
  }, [item.groupOptions, item.value])

  const derivedTimeframeValue = useMemo(() => {
    const timeframe = getAggregationAndTimeframeFromValue(
      item.value,
      item.groupOptions
    )?.timeframe

    return timeframe
  }, [item.groupOptions, item.value])

  const derivedAggregationLabel = useMemo(() => {
    const aggregation = getAggregationAndTimeframeFromValue(
      item.value,
      item.groupOptions
    )?.aggregation

    return aggregation ? uppercaseFirstLetter(aggregation) : ""
  }, [item.groupOptions, item.value])

  const derivedTimeframeLabel = useMemo(() => {
    const timeframe = getAggregationAndTimeframeFromValue(
      item.value,
      item.groupOptions
    )?.timeframe

    return timeframe || ""
  }, [item.groupOptions, item.value])

  const derivedDisplayLabel = useMemo(() => {
    if (item.sparkline) {
      return "Sparkline"
    }
    return "Number"
  }, [item.sparkline])

  const disableSparkline = useMemo(() => {
    if (
      item.value?.includes("change") &&
      !item.value?.includes("24h") &&
      !item.value?.includes("latest")
    ) {
      return false
    }

    return true
  }, [item.value])

  return (
    <MenuButton
      placement="bottom end"
      trigger={
        <IconButton
          icon={DotsThree}
          iconProps={{
            weight: "bold",
          }}
          variant="ghost"
          size="small"
          label="Settings"
          className="!w-7 !h-7 text-fg-secondary"
        />
      }
    >
      <MenuButton.SubmenuTrigger>
        <MenuButton.Item>
          <div className="flex items-center justify-between gap-4">
            Timeframe
            <span className="text-fg-secondary">{derivedTimeframeLabel}</span>
          </div>
        </MenuButton.Item>
        <MenuButton.Popover>
          <MenuButton.Menu
            selectionMode="single"
            selectedKeys={
              derivedTimeframeValue ? [derivedTimeframeValue] : undefined
            }
          >
            {Array.from(splitGroupOptions.timeframes).map((option) => (
              <MenuButton.Item
                key={option}
                id={option}
                onAction={() => {
                  handleSelectTimeframe(option)
                }}
              >
                {option}
              </MenuButton.Item>
            ))}
          </MenuButton.Menu>
        </MenuButton.Popover>
      </MenuButton.SubmenuTrigger>
      <MenuButton.SubmenuTrigger>
        <MenuButton.Item>
          <div className="flex items-center justify-between gap-4">
            Aggregation
            <span className="text-fg-secondary">{derivedAggregationLabel}</span>
          </div>
        </MenuButton.Item>
        <MenuButton.Popover>
          <MenuButton.Menu
            selectionMode="single"
            selectedKeys={
              derivedAggregationValue ? [derivedAggregationValue] : undefined
            }
          >
            {Array.from(splitGroupOptions.aggregations).map((option) => (
              <MenuButton.Item
                key={option}
                id={option}
                onAction={() => {
                  handleSelectAggregation(option)
                }}
              >
                {uppercaseFirstLetter(option)}
              </MenuButton.Item>
            ))}
          </MenuButton.Menu>
        </MenuButton.Popover>
      </MenuButton.SubmenuTrigger>
      <MenuButton.SubmenuTrigger>
        <MenuButton.Item>
          <div className="flex items-center justify-between gap-4">
            Display as
            <span className="text-fg-secondary">{derivedDisplayLabel}</span>
          </div>
        </MenuButton.Item>
        <MenuButton.Popover>
          <MenuButton.Menu
            selectionMode="single"
            selectedKeys={[item.sparkline ? "sparkline" : "number"]}
          >
            <MenuButton.Item
              key="number"
              id="number"
              onAction={() => {
                setDraftSelectedColumns((prev) => {
                  return prev.map((c, i) => {
                    if (i === index) {
                      return { ...c, sparkline: false }
                    }
                    return c
                  })
                })
              }}
            >
              Number
            </MenuButton.Item>
            <MenuButton.Item
              isDisabled={disableSparkline}
              key="sparkline"
              id="sparkline"
              onAction={() => {
                setDraftSelectedColumns((prev) => {
                  return prev.map((c, i) => {
                    if (i === index) {
                      return { ...c, sparkline: true }
                    }
                    return c
                  })
                })
              }}
            >
              Sparkline
            </MenuButton.Item>
          </MenuButton.Menu>
        </MenuButton.Popover>
      </MenuButton.SubmenuTrigger>
    </MenuButton>
  )
}
